// Settings -> Material & Service Management -> Service Rates tab.
//
// Lists EVERY service in the platform catalogue, each showing whichever rate actually applies —
// this tenant's own `service_rate_master` row if it has one, else the platform default. Editing
// a row always writes a TENANT row (upsertServiceRate), which from then on shadows the platform
// default for this tenant only — the platform row itself is never touched, so it keeps serving
// every other tenant that hasn't overridden it. This is the "fork on edit" pattern: nothing
// needs to be explicitly "copied" or "added" first.
//
// The table is server-searched/paginated with a user-adjustable page size.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useListEffectiveServiceRatesQuery,
  useUpsertServiceRateMutation,
  useDeleteServiceRateMutation,
  type EffectiveServiceRate,
} from '@/redux/internal-services/tenantPreferencesApi';
import { apiErrorMessage } from '@/utils/apiError';
import { cn } from '@/utils/cn';
import { Button } from '@/components/UI/Button';
import { Badge } from '@/components/UI/Badge';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { SearchInput } from '@/components/UI/SearchInput';
import { fieldClass } from '@/components/UI/fieldStyles';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const FIELD_CLASS = fieldClass({ size: 'sm' });

const money = (value?: number | null, currency = 'USD') =>
  value === null || value === undefined
    ? '—'
    : new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);

type Toast = { type: 'success' | 'error'; msg: string };

const SourceBadge: React.FC<{ source: EffectiveServiceRate['rate_source'] }> = ({ source }) => {
  const { t } = useTranslation();
  if (source === 'NONE') return null;
  const isTenant = source === 'TENANT_RATE';
  return (
    <Badge variant={isTenant ? 'accent' : 'neutral'} className="ml-2 uppercase">
      {isTenant
        ? t('settings.materialService.serviceRates.tenantBadge', 'Your rate')
        : t('settings.materialService.serviceRates.platformBadge', 'Platform default')}
    </Badge>
  );
};

const RateForm: React.FC<{
  service: EffectiveServiceRate;
  onClose: () => void;
  onToast: (toast: Toast) => void;
}> = ({ service, onClose, onToast }) => {
  const { t } = useTranslation();
  const [upsert, { isLoading }] = useUpsertServiceRateMutation();
  const [baseRate, setBaseRate] = useState(String(service.base_rate ?? ''));
  const [rateUom, setRateUom] = useState(service.rate_uom ?? service.default_uom ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');

  const save = async () => {
    const rate = Number(baseRate);
    if (Number.isNaN(rate)) return;
    try {
      await upsert({
        service_code: service.service_code,
        base_rate: rate,
        rate_uom: rateUom || undefined,
        effective_from: effectiveFrom || undefined,
        effective_to: effectiveTo || undefined,
      }).unwrap();
      onToast({ type: 'success', msg: t('settings.materialService.serviceRates.saved', 'Service rate saved.') });
      onClose();
    } catch (error) {
      onToast({
        type: 'error',
        msg: apiErrorMessage(error, t, t('settings.materialService.serviceRates.saveFailed', 'That rate could not be saved.')),
      });
    }
  };

  return (
    <tr className="bg-ui-subtle">
      <td colSpan={4} className="p-3">
        {service.rate_source === 'PLATFORM_RATE' && (
          <p className="mb-3 text-xs text-ui-text2">
            {t(
              'settings.materialService.serviceRates.forkNotice',
              'Saving creates YOUR OWN rate for this service — the platform default is unaffected and keeps applying to every other tenant.'
            )}
          </p>
        )}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-ui-text">
              {t('settings.materialService.serviceRates.rate', 'Rate')}
            </label>
            <input
              type="number"
              step="0.01"
              value={baseRate}
              onChange={(e) => setBaseRate(e.target.value)}
              className={FIELD_CLASS}
              autoFocus
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ui-text">
              {t('settings.materialService.serviceRates.uom', 'Unit')}
            </label>
            <input type="text" value={rateUom} onChange={(e) => setRateUom(e.target.value)} className={FIELD_CLASS} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ui-text">
              {t('settings.materialService.serviceRates.effectiveFrom', 'Effective from')}
            </label>
            <input
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              className={FIELD_CLASS}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-ui-text">
              {t('settings.materialService.serviceRates.effectiveTo', 'Effective to (optional)')}
            </label>
            <input
              type="date"
              value={effectiveTo}
              onChange={(e) => setEffectiveTo(e.target.value)}
              className={FIELD_CLASS}
            />
          </div>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button size="sm" loading={isLoading} onClick={save} disabled={!baseRate}>
            {t('common.save', 'Save')}
          </Button>
        </div>
      </td>
    </tr>
  );
};

const ServiceRatesSection: React.FC<{ onToast: (toast: Toast) => void }> = ({ onToast }) => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[1]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isFetching } = useListEffectiveServiceRatesQuery({
    search: search || undefined,
    skip: page * pageSize,
    limit: pageSize,
  });
  const [deleteRate] = useDeleteServiceRateMutation();
  const [editingServiceCode, setEditingServiceCode] = useState<string | null>(null);

  const rates = data?.items ?? [];
  const total = data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);

  const remove = async (rate: EffectiveServiceRate) => {
    if (!rate.service_rate_code) return;
    if (!window.confirm(t('settings.materialService.serviceRates.confirmRevert', 'Revert this service back to the platform default rate?'))) return;
    try {
      await deleteRate(rate.service_rate_code).unwrap();
      onToast({ type: 'success', msg: t('settings.materialService.serviceRates.reverted', 'Reverted to the platform default.') });
    } catch (error) {
      onToast({
        type: 'error',
        msg: apiErrorMessage(error, t, t('settings.materialService.serviceRates.deleteFailed', 'That rate could not be removed.')),
      });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-ui-text">
          {t('settings.materialService.serviceRates.title', 'Service rates')}
        </h3>
        <p className="mt-1 text-xs text-ui-text2">
          {t(
            'settings.materialService.serviceRates.description',
            'Every service and its current rate. Editing one sets your own rate for it; until then it uses the platform default.'
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder={t('settings.materialService.serviceRates.searchService', 'Search services…')}
          wrapperClassName="min-w-[240px] max-w-none flex-1"
        />
        <label className="flex items-center gap-2 text-xs text-ui-text2">
          {t('settings.materialService.rowsPerPage', 'Rows per page')}
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            className={cn(fieldClass({ size: 'sm' }), 'w-auto')}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="table-card overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr>
              <th className="th-cell">{t('settings.materialService.serviceRates.service', 'Service')}</th>
              <th className="th-cell">{t('settings.materialService.serviceRates.rate', 'Rate')}</th>
              <th className="th-cell">{t('settings.materialService.serviceRates.source', 'Source')}</th>
              <th className="th-cell" />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={4}>
                  <LoadingState label={t('common.loading', 'Loading…')} className="py-6" />
                </td>
              </tr>
            )}
            {!isLoading && rates.length === 0 && (
              <tr>
                <td colSpan={4}>
                  <EmptyState title={t('common.noResults', 'No matches.')} className="py-6" />
                </td>
              </tr>
            )}
            {rates.map((rate) =>
              editingServiceCode === rate.service_code ? (
                <RateForm
                  key={rate.service_code}
                  service={rate}
                  onClose={() => setEditingServiceCode(null)}
                  onToast={onToast}
                />
              ) : (
                <tr key={rate.service_code}>
                  <td className="td-cell">{rate.service_name}</td>
                  <td className="td-cell">
                    {money(rate.base_rate, rate.currency)}
                    {rate.rate_uom ? ` /${rate.rate_uom}` : ''}
                  </td>
                  <td className="td-cell">
                    <SourceBadge source={rate.rate_source} />
                  </td>
                  <td className="td-cell text-right">
                    <Button
                      type="button"
                      variant="soft"
                      size="xs"
                      onClick={() => setEditingServiceCode(rate.service_code)}
                    >
                      {t('common.edit', 'Edit')}
                    </Button>
                    {rate.rate_source === 'TENANT_RATE' && (
                      <Button
                        type="button"
                        variant="destructive-ghost"
                        size="xs"
                        onClick={() => void remove(rate)}
                        className="ml-1"
                      >
                        {t('settings.materialService.serviceRates.revert', 'Revert to default')}
                      </Button>
                    )}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div className="flex items-center justify-between text-xs text-ui-text2">
          <span>
            {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} {t('common.of', 'of')} {total}
            {isFetching && ` · ${t('settings.materialService.updating', 'updating…')}`}
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => setPage((previous) => Math.max(0, previous - 1))}
              disabled={page === 0}
            >
              {t('common.previous', 'Previous')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => setPage((previous) => Math.min(lastPage, previous + 1))}
              disabled={page >= lastPage}
            >
              {t('common.next', 'Next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceRatesSection;
